/* @flow */

import { action, observable } from 'mobx';
import hash from 'object-hash';
import moment from 'moment';
import to from 'to-case';

import { ApiModel } from 'api/ApiModel';
import type {
  ApiType,
  AvailableFilter,
  AvailableGroupedFilter,
  ConsolidatedFilterItem,
  ServerFilterType,
  SortSpec,
  SupportedUriTokenNames,
  UserFilterItem,
  UserSearchPreference,
} from 'tables/components/ReportView/types';
import { DataViewConfig } from 'tables/components/ReportView/types';
import { actionDrawerStore, errorHandlerService } from 'services/ServiceRegistry';
import { convertToSearchString } from 'utils/uri';

const CACHE_PERIOD = 300000; // 5 minutes
const DEFAULT_PAGE_SIZE = 20;

const FILTER_KEY_LABEL_SEPERATOR = '$$';
const FILTER_VALUE_LABEL_SEPERATOR = '$#';

// Treat "all" (any case), empty strings, null and undefined as "no filter".
const isAll = (v: any): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim().length === 0) ||
  String(v)
    .trim()
    .toLowerCase() === 'all';

export type DataStoreEntryBase = {
  codename: string,
  loading: boolean,
  firstLoad: boolean,
  ts: Date,
};

export type CurrentSearchItem = {
  id: string,
  label?: string,
};

export type DataStoreEntry = DataStoreEntryBase & {
  availableFilters?: AvailableFilter[],
  currentSearch?: { [key: string]: CurrentSearchItem[] },
  data?: any[],
  meta: {
    currentPage: number,
    lastPage: number,
    perPage: number | null,
    totalResults: number | null,
    tabCounts?: { [string]: number },
  },
  filtersLoading: boolean,
};

export type SummaryDataStoreEntry = DataStoreEntryBase & {
  data?: any[],
};

export type LoadDataOptionsBase = {
  codename: string,
};

export type LoadDataOptions = LoadDataOptionsBase & {
  filters?: ConsolidatedFilterItem[],
  search?: string,
  sort?: SortSpec,
  page: number,
};

export type LoadSummaryDataOptions = LoadDataOptionsBase & {
  [x: SupportedUriTokenNames | 'codename' | 'region']: string,
};

export const DATE_FORMAT = 'YYYY-MM-DD';

export const timeRangeToString = (from?: string | Date, to?: string | Date): string => {
  if (!from && !to) {
    return '';
  }

  return `${from ? moment(from).format(DATE_FORMAT) : ''}--${
    to ? moment(to).format(DATE_FORMAT) : ''
  }`;
};

const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const groupByKey = (list, key) =>
  list.reduce((hash, obj) => ({ ...hash, [obj[key]]: (hash[obj[key]] || []).concat(obj) }), {});

export const stringToTimeRange = (str: string): { from: string, to: string } => {
  const [from, to] = str.split('--');

  return {
    from,
    to,
  };
};

export const loadSummaryOptionsToFilterItems = (
  options: LoadSummaryDataOptions
): ConsolidatedFilterItem[] => {
  const items: ConsolidatedFilterItem[] = [];

  // Keep only meaningful values (not "all"/empty)
  if (!isAll(options.region)) {
    items.push({ key: 'region_id', values: [options.region] });
  }
  if (!isAll(options.brandId)) {
    items.push({ key: 'brand_id', values: [options.brandId] });
  }
  if (!isAll(options.accountId)) {
    items.push({ key: 'account_id', values: [options.accountId] });
  }

  return items;
};

const generateKey = (
  dataPathname: string,
  type: 'data' | 'summary',
  options: LoadDataOptions | LoadSummaryDataOptions
) => {
  const _options = {
    ...options,
  };

  switch (type) {
    case 'data':
      if (_options.filters) {
        _options.filters = _options.filters.map(filter => {
          const _filter = filter;
          delete _filter.userFilterId;
          delete _filter.isFixed;
          delete _filter.doesNotMatchAvailableFilters;

          return _filter;
        });
      }

      break;

    case 'summary':
    default:
      break;
  }

  return `${_options.codename}-${hash.sha1({ dataPathname, _options })}`;
};

type ConvertableFilterItem = { key: string, values: string[] };

const generateQueryString = (apiType: ApiType, options: LoadDataOptions) => {
  const formattedOptions: { [x: string]: string } = {};

  switch (apiType) {
    case 'AzureSearch': {
      formattedOptions.top = String(DEFAULT_PAGE_SIZE);
      formattedOptions.skip = String(Math.max((options.page || 1) - 1, 0) * DEFAULT_PAGE_SIZE);
      if (options.sort) {
        formattedOptions.orderBy = options.sort.columnName;
        formattedOptions.sortedBy = options.sort.direction.toLowerCase();
      }
      break;
    }

    case 'SQL':
    default: {
      if (typeof options.page !== 'undefined') {
        formattedOptions.page = String(options.page);
      }
      if (options.sort) {
        formattedOptions.orderBy = options.sort.columnName;
        formattedOptions.sortedBy = options.sort.direction.toLowerCase();
      }
      break;
    }
  }

  let searchString: ?string = options.search || '';

  // Remove "all"/empty values from filters before encoding
  if (options.filters && options.filters.length) {
    const sanitizedFilters = options.filters
      .map(filter => ({
        ...filter,
        values: (filter.values || []).filter(v => !isAll(v)),
      }))
      .filter(f => Array.isArray(f.values) && f.values.length > 0);

    if (sanitizedFilters.length) {
      const filtersStr = convertToSearchString(sanitizedFilters);
      searchString = `${searchString ? `${searchString};` : ''}${filtersStr}`;
    }
  }

  if (searchString) {
    formattedOptions.search = searchString;
  }

  const params = new URLSearchParams(formattedOptions);
  return params.toString();
};

export default class ReportViewStore extends ApiModel {
  @observable loading = {
    saveUserSearchPreference: false,
    exportSummary: false,
  };

  @observable shouldInvalidateCache = false;

  @observable userSearchPreferences: Map<string, UserSearchPreference[]> = new Map();

  @observable dataStore: Map<string, DataStoreEntry> = new Map();

  @observable summaryDataStore: Map<string, SummaryDataStoreEntry> = new Map();

  @action initDataStoreEntry = (storeKey: string, options: LoadDataOptions) => {
    let dataStoreEntry = this.dataStore.get(storeKey) || {};

    dataStoreEntry.loading = true;
    dataStoreEntry.filtersLoading = true;
    dataStoreEntry.codename = options.codename;

    if (!this.dataStore.has(storeKey)) {
      dataStoreEntry.meta = {
        currentPage: options.page,
        lastPage: options.page,
        perPage: null,
        totalResults: null,
      };
      dataStoreEntry.firstLoad = true;
      dataStoreEntry.availableFilters = [];
    } else if (Date.now() - dataStoreEntry.ts.getTime() >= CACHE_PERIOD) {
      dataStoreEntry.data = undefined;
    }

    dataStoreEntry.ts = new Date();
    this.dataStore.set(storeKey, dataStoreEntry);
  };

  @action initSummaryStoreEntry = (storeKey: string, options: LoadSummaryDataOptions) => {
    let dataStoreEntry = this.summaryDataStore.get(storeKey) || {};

    dataStoreEntry.loading = true;
    dataStoreEntry.codename = options.codename;

    if (!this.summaryDataStore.has(storeKey)) {
      dataStoreEntry.firstLoad = true;
      dataStoreEntry.data = undefined;
    } else if (Date.now() - dataStoreEntry.ts.getTime() >= CACHE_PERIOD) {
      dataStoreEntry.data = undefined;
    }

    dataStoreEntry.ts = new Date();
    this.summaryDataStore.set(storeKey, dataStoreEntry);
  };

  // TODO: When TS arrives, update parameter `dataStoreEntry` to be partial and exclude key `ts`
  @action updateDataStoreEntryData = (storeKey: string, dataStoreEntry: DataStoreEntry) => {
    const existingDataStoreEntry = this.dataStore.get(storeKey);

    if (existingDataStoreEntry) {
      if (dataStoreEntry.data) {
        existingDataStoreEntry.data = dataStoreEntry.data;
      }

      if (dataStoreEntry.meta) {
        existingDataStoreEntry.meta = dataStoreEntry.meta;
      }

      if (typeof dataStoreEntry.loading !== 'undefined') {
        existingDataStoreEntry.loading = dataStoreEntry.loading;
      }
      if (typeof dataStoreEntry.filtersLoading !== 'undefined') {
        existingDataStoreEntry.filtersLoading = dataStoreEntry.filtersLoading;
      }
      if (typeof dataStoreEntry.firstLoad !== 'undefined') {
        existingDataStoreEntry.firstLoad = dataStoreEntry.firstLoad;
      }

      if (dataStoreEntry.availableFilters) {
        existingDataStoreEntry.availableFilters = dataStoreEntry.availableFilters;
      }

      if (dataStoreEntry.currentSearch) {
        existingDataStoreEntry.currentSearch = dataStoreEntry.currentSearch;
      }

      existingDataStoreEntry.ts = new Date();
      this.dataStore.set(storeKey, existingDataStoreEntry);
    }
  };

  @action updateSummaryDataStoreEntryData = (
    storeKey: string,
    dataStoreEntry: SummaryDataStoreEntry
  ) => {
    const existingDataStoreEntry = this.summaryDataStore.get(storeKey);

    if (existingDataStoreEntry) {
      if (dataStoreEntry.data) {
        existingDataStoreEntry.data = dataStoreEntry.data;
      }

      if (typeof dataStoreEntry.loading !== 'undefined') {
        existingDataStoreEntry.loading = dataStoreEntry.loading;
      }
      if (typeof dataStoreEntry.firstLoad !== 'undefined') {
        existingDataStoreEntry.firstLoad = dataStoreEntry.firstLoad;
      }

      existingDataStoreEntry.ts = new Date();
      this.summaryDataStore.set(storeKey, existingDataStoreEntry);
    }
  };

  @action loadUserSearchPreference = async (userFilterNamespace: string) => {
    await this.api
      .getResource(`user-search-preferences/${userFilterNamespace}`)
      .then(searchPreferences => {
        this.userSearchPreferences.set(
          userFilterNamespace,
          searchPreferences.map(searchPreference => ({
            id: searchPreference.id,
            createdAt: searchPreference.created_at,
            model: searchPreference.model,
            name: searchPreference.name,
            searchConfig: {
              filters:
                (searchPreference.search_config &&
                  searchPreference.search_config.parameters &&
                  typeof searchPreference.search_config.parameters.map === 'function' &&
                  searchPreference.search_config.parameters.map(parameter => ({
                    key: parameter.key,
                    type: parameter.type,
                    value:
                      parameter.type === 'date' || parameter.to || parameter.from
                        ? timeRangeToString(parameter.from, parameter.to)
                        : parameter.value,
                  }))) ||
                [],
              search:
                searchPreference.search_config &&
                searchPreference.search_config.terms.filter(term => typeof term === 'string'),
            },
            updatedAt: searchPreference.updated_at,
            userId: searchPreference.user_id,
          }))
        );
      });
  };

  @action
  saveUserSearchPreference = async (
    userFilterNamespace: string,
    name: string,
    searchConfig: {
      filters: UserFilterItem[],
      search: string[],
    },
    id?: string
  ) => {
    this.loading.saveUserSearchPreference = true;
    try {
      const payload = {
        name,
        model: userFilterNamespace,
        search_config: {
          label: name,
          terms: searchConfig.search,
          filterIds: searchConfig.filters.map(filter => filter.key),
          parameters: searchConfig.filters.map(filter => ({
            key: filter.key,
            value: filter.value,
          })),
        },
      };

      let result;
      if (id) {
        payload.id = id;
        result = await this.api.putResource(`user-search-preferences/${id}`, payload);
      } else {
        result = await this.api.createResource('user-search-preferences', payload);
      }

      await this.loadUserSearchPreference(userFilterNamespace);

      return id || result.id;
    } catch (error) {
    } finally {
      this.loading.saveUserSearchPreference = false;
    }
  };

  removeUserSearchPreference = async (id: string, userFilterNamespace: string) => {
    const result = await this.api.deleteResource(`user-search-preferences/${id}`);
    await this.loadUserSearchPreference(userFilterNamespace);

    return result;
  };

  getFiltersForRender = (availableValues, type: ServerFilterType) => {
    return availableValues
      .map(facetItem => {
        const filterKeyItem = (facetItem.value || '').split(FILTER_KEY_LABEL_SEPERATOR);
        const { value } = facetItem;
        const label =
          type === 'date'
            ? capitalizeFirstLetter(facetItem.label)
            : capitalizeFirstLetter(filterKeyItem[0].trim());

        return {
          value,
          label,
          ...(type === 'date' ? { from: facetItem.from, to: facetItem.to } : {}),
        };
      })
      .filter(filterItem => {
        const filterValueItem = (filterItem.value || '').split(FILTER_VALUE_LABEL_SEPERATOR);
        const filterValueIsSubGroup = filterValueItem.length > 1;

        return !filterValueIsSubGroup;
      });
  };

  getSubGroupFiltersForRender = (availableValues, key): AvailableGroupedFilter => {
    const ungroupedFilters = availableValues
      .map(facetItem => {
        const filterKeyItem = (facetItem.value || '').split(FILTER_VALUE_LABEL_SEPERATOR);

        let name;
        if (filterKeyItem.length > 1) {
          name = filterKeyItem[1].split(FILTER_KEY_LABEL_SEPERATOR)[0].replace('#', '');
        }

        return {
          name,
          key,
          label: filterKeyItem[0],
          value: facetItem.value,
          splitFilterItem: filterKeyItem,
        };
      })
      .filter(item => item.splitFilterItem.length > 1)
      .map(item => {
        delete item.splitFilterItem;
        return { ...item, isGroup: true };
      });

    return groupByKey(ungroupedFilters, 'name');
  };

  loadData = (
    viewConfig: DataViewConfig,
    options: LoadDataOptions,
    extraOptions: { filtersPathnameOverride?: string, disableFilters?: boolean } = {}
  ): string => {
    const { filtersPathnameOverride, disableFilters } = extraOptions;
    const { dataPathname, apiType } = viewConfig;
    const storeKey = generateKey(dataPathname, 'data', options);
    this.initDataStoreEntry(storeKey, options);

    if (!disableFilters && apiType === 'SQL') {
      const filterOptions = {
        ...options,
      };
      delete filterOptions.page;
      delete filterOptions.sort;
      const filterQueryString = generateQueryString(apiType, filterOptions);
      const path = filtersPathnameOverride
        ? `${filtersPathnameOverride}?${filterQueryString}`
        : `${dataPathname}/filters?${filterQueryString}`;
      this.api.getResource(path, { fullResponse: true }).then(({ data, current_search }) => {
        this.updateDataStoreEntryData(storeKey, {
          filtersLoading: false,
          availableFilters: data.map(result => ({
            ...result,
            filters: result.filters.map(resultFilter => ({
              label: resultFilter.value,
              value: resultFilter.id,
              to: resultFilter.to,
              from: resultFilter.from,
            })),
          })),
          currentSearch: current_search,
        });
      });
    }

    this.api
      .getPagedResource(this.formulateUrl(apiType, dataPathname, options))
      .then(results => {
        switch (apiType) {
          case 'AzureSearch': {
            const totalResults = results['@odata.count'] || 0;
            const azureResults =
              results &&
              results.value &&
              results.value.map(item => ({
                id: item[viewConfig.idKey],
                ...item,
              }));
            const entry: $Shape<DataStoreEntry> = {
              data: azureResults || [],
              loading: false,
              firstLoad: false,
              filtersLoading: false,
              currentSearch: results.current_search,
              meta: {
                currentPage: options.page,
                lastPage: Math.ceil(totalResults / DEFAULT_PAGE_SIZE),
                perPage: DEFAULT_PAGE_SIZE,
                totalResults,
              },
              availableFilters:
                (results &&
                  results['@search.facets'] &&
                  Object.entries(results['@search.facets']).map(
                    ([key, availableValues]: [string, any[]]) => {
                      const type: ServerFilterType = key.includes('date') ? 'date' : 'string';
                      return {
                        key,
                        name: to.title(key),
                        type,
                        filters: this.getFiltersForRender(availableValues, type),
                        groupedFilters: this.getSubGroupFiltersForRender(availableValues, key),
                      };
                    }
                  )) ||
                [],
            };

            this.updateDataStoreEntryData(storeKey, entry);
            break;
          }

          default:
          case 'SQL': {
            let resultType = 'legacy';
            if (typeof results.meta === 'object') {
              resultType = 'current';
            }

            switch (resultType) {
              case 'legacy': {
                const meta = {
                  currentPage: results.current_page,
                  lastPage: results.last_page,
                  perPage: results.per_page,
                  totalResults: results.total,
                };

                if (results.counts) {
                  meta.tabCounts = results.counts;
                }

                const entry: DataStoreEntry = {
                  data: results.data,
                  meta,
                  loading: false,
                  firstLoad: false,
                };

                if (disableFilters) {
                  entry.filtersLoading = false;
                }

                this.updateDataStoreEntryData(storeKey, entry);
                break;
              }

              case 'current': {
                const entry: DataStoreEntry = {
                  data: results.data,
                  meta: {
                    currentPage: results.meta.current_page,
                    lastPage: results.meta.last_page,
                    perPage: results.meta.per_page,
                    totalResults: results.meta.total,
                  },
                  loading: false,
                  firstLoad: false,
                };

                if (results.counts) {
                  entry.meta.tabCounts = results.counts;
                }

                if (disableFilters) {
                  entry.filtersLoading = false;
                }

                this.updateDataStoreEntryData(storeKey, entry);
                break;
              }

              default:
                break;
            }

            break;
          }
        }
      })
      .catch(err => {
        errorHandlerService.handleException(err, 'ReportViewStore#loadData');
      });

    return storeKey;
  };

  loadSummaryData = (dataPathname: string, options: LoadSummaryDataOptions): string => {
    const storeKey = generateKey(dataPathname, 'summary', options);

    this.initSummaryStoreEntry(storeKey, options);

    let searchString = '';

    const searchParams = loadSummaryOptionsToFilterItems(options);

    if (searchParams.length > 0) {
      searchString = convertToSearchString(searchParams);
    }

    const paramsObject = {};

    if (searchString) {
      paramsObject.search = searchString;
    }

    const urlParams = new URLSearchParams(paramsObject);

    this.api
      .getPagedResource(`${dataPathname}?${urlParams.toString()}`)
      .then(result => {
        this.updateSummaryDataStoreEntryData(storeKey, {
          loading: false,
          firstLoad: false,
          data: result && result.data ? result.data : [],
        });
      })
      .catch(err => {
        errorHandlerService.handleException(err, 'ReportViewStore#loadSummaryData');

        this.updateSummaryDataStoreEntryData(storeKey, {
          loading: false,
          firstLoad: false,
          data: [],
        });
      });

    return storeKey;
  };

  @action
  invalidateCacheByCodename = (codename: string) => {
    const matchingKeys = Array.from(this.dataStore.keys()).filter(key => key.startsWith(codename));

    for (const matchingKey of matchingKeys) {
      this.dataStore.delete(matchingKey);
    }
  };

  formulateUrl = (apiType: ApiType, dataPathname: string, options: LoadDataOptions) => {
    const queryString = generateQueryString(apiType, options);

    return `${dataPathname}${dataPathname.includes('?') ? '&' : '?'}${queryString}`;
  };

  @action
  exportSummary = async (
    base: 'overall-stats' | 'brand-stats' | 'account-stats',
    options: LoadSummaryDataOptions
  ) => {
    this.loading.exportSummary = true;

    try {
      const filterItems: ConvertableFilterItem[] = [];
      const params = new URLSearchParams();
      const filenameItems: string[] = [];

      if (!isAll(options.region)) {
        filterItems.push({ key: 'region_id', values: [options.region] });
        filenameItems.push(String(options.region));
      }

      if (!isAll(options.brandId)) {
        filterItems.push({ key: 'brand_id', values: [options.brandId] });
        filenameItems.push(String(options.brandId));
      }

      if (!isAll(options.accountId)) {
        filterItems.push({ key: 'account_id', values: [options.accountId] });
        filenameItems.push(String(options.accountId));
      }

      if (filterItems.length) {
        params.set('search', convertToSearchString(filterItems));
      }

      const queryString = params.toString();
      const exportUrl = `${base}/export/pdf${queryString ? `?${queryString}` : ''}`;
      const fileName = `${base}${filenameItems.length ? '-' : ''}${filenameItems.join('-')}`;

      await actionDrawerStore.exportPdfFile(exportUrl, fileName);
    } catch (err) {
      errorHandlerService.handleException(err, 'ReportViewStore#exportSummary');
    } finally {
      this.loading.exportSummary = false;
    }
  };
}
